import cloneDeep from 'lodash/cloneDeep';

import { ProjectSelectOption } from '@qality/common-model';
import Project from '@qality/common-model/src/Project';
import { RecentAndMoreProjects } from '@qality/common-model/src/RecentAndMoreProjects';
import { ProjectService } from '@qality/common-service';

import { ProjectSelectGroupedOptions } from './RecentAndMoreProjects.d';

export const mapProjectsToSelectOptions = (
  projects: RecentAndMoreProjects
): ProjectSelectGroupedOptions => {
  const { recentProjects, moreProjects } = projects;

  const recentProjectsMapped =
    recentProjects?.map((project) => {
      return mapProjectToSelectOption(project);
    }) ?? [];

  const moreProjectsMapped =
    moreProjects.map((project) => {
      return mapProjectToSelectOption(project);
    }) ?? [];

  return [
    {
      label: 'Recent projects',
      options: recentProjectsMapped,
    },
    {
      label: recentProjectsMapped.length === 0 ? '' : 'More projects',
      options: moreProjectsMapped,
      areMoreProjectsAvailable: true,
    },
  ];
};

export const mapMoreProjectsToSelectOptions = (
  newProjects: Array<Project>,
  selectDefaultOptions: ProjectSelectGroupedOptions,
  projectsCount: {
    currentRecent: number;
    currentMore: number;
    wantedTotal: number;
  }
): ProjectSelectGroupedOptions => {
  const [recentProjectsGroup, _moreProjectsGroup] = selectDefaultOptions;

  // filter out projects from 'recent' group if needed
  const newMoreProjects =
    projectsCount.currentRecent > 0
      ? newProjects.filter((newProject) => {
          return !recentProjectsGroup.options.find((recentProject) => {
            return newProject.id === recentProject.id;
          });
        })
      : newProjects;

  // crop 'new more projects' to desired length
  // map 'new more projects' to select options
  const newMoreProjectsSelectOptions = newMoreProjects
    .slice(0, projectsCount.wantedTotal - projectsCount.currentRecent)
    .map(mapProjectToSelectOption);

  // verify if new 'more project' were added
  const areMoreProjectsAvailable =
    newMoreProjectsSelectOptions.length > projectsCount.currentMore;

  return [
    recentProjectsGroup,
    {
      label: 'More projects',
      options: newMoreProjectsSelectOptions,
      areMoreProjectsAvailable,
    },
  ];
};

export const mapProjectToSelectOption = (
  project: Project
): ProjectSelectOption => {
  return {
    label: `${project.name} (${project.key})`,
    value: project.id,
    name: project.name,
    id: project.id,
    key: project.key,
    iconUrl: project.avatarUrls['16x16'],
  };
};

export const addProjectFromValue = (
  projectsOptions: ProjectSelectGroupedOptions,
  value: ProjectSelectOption
): ProjectSelectGroupedOptions => {
  const projects = cloneDeep(projectsOptions);

  const project = [
    ...(projects[0]?.options ?? []),
    ...(projects[1]?.options ?? []),
  ].find((projectItem) => projectItem.id.toString() === value.id);

  if (project) {
    return projects;
  }

  projects[0].options.push(value);

  return projects;
};

export const addProjectFromUrl = async (
  projectsOptions: ProjectSelectGroupedOptions,
  projectId: string
): Promise<{
  projects: ProjectSelectGroupedOptions;
  project: ProjectSelectOption;
}> => {
  const projects = cloneDeep(projectsOptions);

  // 2 cases
  // 1: we are in a project -> it will be in recent projects
  // 2: we got a link to some E.g report with projectId -> it will NOT be present in recent since we never opened it
  const oldProjectSelectOption = [
    ...(projects[0]?.options ?? []),
    ...(projects[1]?.options ?? []),
  ].find((projectItem) => projectItem.id.toString() === projectId);

  if (oldProjectSelectOption) {
    return { projects, project: oldProjectSelectOption };
  }

  const project = await ProjectService.getProject(projectId);
  const newProjectSelectOption = mapProjectToSelectOption(project);

  projects[0].options.push(newProjectSelectOption);

  return { projects, project: newProjectSelectOption };
};
